import { ref } from "vue"
import { FileRepository } from "@/repositories/file-repository"
import { File as FileModel } from "@/models/file/file.model"
import { FileRelation } from "@/models/file/file-relation.model"
import { useDialogStore } from "@/store/dialog.store"
import { DialogTypes } from "@/interfaces/component/dialog.interface"
import { FileUploadContexts } from "@/enums/file-upload-contexts"
import { ModelFactory } from "@/factories/model-factory"
import { FileBucketRepository } from "@/repositories/file-bucket-repository"
import { FileBucket } from "@/models/file-bucket.model"
import { fileTypeFromBuffer } from "file-type"
import { Capacitor } from "@capacitor/core"
import type { IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface.ts"

export interface UseFileUploadOptions {
  /**
   * Accept attribute for file input element (e.g., "image/png, image/jpeg", "application/pdf").
   * If provided, this will override the default allowed types validation.
   */
  accept?: string
}

/**
 * Vue composable for handling file uploads.
 *
 * @param managed - Whether to manage the uploaded files using ModelManager.
 * @param options - Optional configuration options.
 * @returns {Object} An object that includes the uploading status and the upload function.
 */
export function useFileUpload(managed = true, options?: UseFileUploadOptions) {
  const isUploading = ref(false)

  async function upload(
    fileData: File | File[],
    fileUploadContext: FileUploadContexts,
    onFileUpload?: (fileRelation: FileRelation) => void
  ) {
    if (fileData instanceof File) {
      fileData = [fileData]
    }

    if (!filesAreValid(fileData)) {
      displayErrorDialog()
      return Promise.reject(new Error("One or more files are invalid."))
    }

    isUploading.value = true

    try {
      for (let data of fileData) {
        data = await fixFileType(data)

        const response = await _uploadFileWithBase64Fallback(data)

        const file = ModelFactory.createModel(FileModel, response!.data, managed) // Create a new File.

        // Create FileRelation model and associate it with the uploaded file.
        const fileRelation = ModelFactory.createModel(FileRelation, undefined, managed)
        fileRelation.file = file
        fileRelation.context = fileUploadContext

        if (onFileUpload) {
          onFileUpload(fileRelation)
        }
      }
    } catch (error) {
      console.error(error)
      return Promise.reject(error)
    } finally {
      isUploading.value = false
    }
  }

  /**
   * Asynchronously uploads one or more files, and optionally calls a callback function for each file after its upload.
   *
   * @param {File | File[]} fileData - The file or array of files to be uploaded.
   * @param {BaseModel} fileBucket - The file bucket to which the file(s) should be associated.
   * @param {FileUploadContexts} fileUploadContext - The context in which the file(s) are uploaded.
   * @param {(fileRelation: FileRelation) => void} [onFileUpload] - Optional callback function to be called after each file upload.
   *
   * @returns {Promise} Resolves when all file uploads are successful, or rejects if an error occurs during upload.
   */
  async function uploadAndCreateRelation(
    fileData: File | File[],
    fileBucket: FileBucket,
    fileUploadContext: FileUploadContexts,
    onFileUpload?: (fileRelation: FileRelation) => void
  ) {
    if (fileData instanceof File) {
      fileData = [fileData]
    }

    if (!filesAreValid(fileData)) {
      displayErrorDialog()
      return Promise.reject(new Error("One or more files are invalid."))
    }

    isUploading.value = true

    try {
      for (let data of fileData) {
        data = await fixFileType(data)

        // Create local FileRelation so uuid can be associated with the uploaded file....
        let fileRelation = ModelFactory.createModel(FileRelation, undefined, managed)

        // Pass the locally created file relation id to the server so it will return a file relation with the same id.
        // This is to prevent duplicate file relations being created when the corresponding model create socket message
        // is received.
        const query = { file_relation_id: fileRelation.id! }

        const response = await _uploadFileToBucketWithBase64Fallback(
          fileBucket,
          data,
          fileUploadContext,
          { query }
        )

        fileRelation = ModelFactory.createModel(FileRelation, response!.data, managed)

        if (onFileUpload) {
          onFileUpload(fileRelation)
        }
      }
    } catch (error) {
      console.error(error)
      return Promise.reject(error)
    } finally {
      isUploading.value = false
    }
  }

  function _uploadFileWithBase64Fallback(fileData: File) {
    let formUploadFailed = false

    // Try form based upload if not on Capacitor.
    if (!Capacitor.isNativePlatform()) {
      try {
        return new FileRepository().uploadFile(fileData)
      } catch (error) {
        console.error(error)
        formUploadFailed = true
      }
    }

    // If Capacitor is used or above form upload failed, fallback to base64 upload.
    if (Capacitor.isNativePlatform() || formUploadFailed) {
      return new FileRepository().uploadFileBase64(fileData)
    }
  }

  function _uploadFileToBucketWithBase64Fallback(
    fileBucket: FileBucket,
    fileData: File,
    fileUploadContext: FileUploadContexts,
    config: IRepositoryRequestConfig = {}
  ) {
    let formUploadFailed = false

    // Try form based upload if not on Capacitor.
    if (!Capacitor.isNativePlatform()) {
      try {
        return new FileBucketRepository().uploadToFileBucket(
          fileBucket,
          fileData,
          fileUploadContext,
          config
        )
      } catch (error) {
        console.error(error)
        formUploadFailed = true
      }
    }

    // If Capacitor is used or above form upload failed, fallback to base64 upload.
    if (Capacitor.isNativePlatform() || formUploadFailed) {
      return new FileBucketRepository().uploadToFileBucketBase64(
        fileBucket,
        fileData,
        fileUploadContext
      )
    }
  }

  async function fixFileType(fileData: File) {
    if (!Capacitor.isNativePlatform()) {
      return fileData
    }
    // Workaround for Android:
    // When dragging a file from one browser to another, the file metadata is corrupted and uploading it will
    // result in a network error. Recreating the file object with the original name and size fixes this issue.
    const arrayBuffer = await fileData.arrayBuffer()
    const result = await fileTypeFromBuffer(new Uint8Array(arrayBuffer))
    if (result) {
      console.log(`Detected MIME type: ${result.mime}`)
      return new File([fileData], fileData.name, { type: result.mime })
    }
    return fileData
  }

  function isFileTypeValid(file: File, acceptType: string): boolean {
    // Handle wildcards like image/*
    if (acceptType.endsWith("/*")) {
      const baseType = acceptType.slice(0, -2)
      return file.type.startsWith(baseType)
    }
    // Handle file extensions like .pdf
    if (acceptType.startsWith(".")) {
      const extension = acceptType.slice(1).toLowerCase()
      const fileExtension = file.name.split(".").pop()?.toLowerCase()
      return fileExtension === extension
    }
    // Handle MIME types
    return file.type === acceptType
  }

  function validateFileType(file: File): boolean {
    if (options?.accept) {
      const acceptedTypes = options.accept.split(",").map((type) => type.trim())
      const isValid = acceptedTypes.some((type) => isFileTypeValid(file, type))

      if (!isValid) {
        console.error(
          `File "${file.name}" has an invalid type (${file.type}). Allowed types are: ${options.accept}.`
        )
      }
      return isValid
    }

    // Default validation using file extensions
    const fileParts = file.name.split(".")
    if (fileParts.length === 0) {
      console.error(`File "${file.name}" has no extension.`)
      return false
    }

    const fileExtension = fileParts.pop()!.toLowerCase()
    if (!getAllowedTypes().includes(fileExtension)) {
      console.error(
        `File "${file.name}" has an invalid extension (${fileExtension}). Allowed extensions are: ${getAllowedTypes().join(", ")}.`
      )
      return false
    }

    return true
  }

  function validateFileSize(file: File): boolean {
    if (file.size > getMaximumFileSize()) {
      console.error(
        `File "${file.name}" exceeds the maximum file size of ${getMaximumFileSize() / 1024 / 1024} MB.`
      )
      return false
    }
    return true
  }

  function filesAreValid(files: File[]): boolean {
    return files.every((file) => validateFileType(file) && validateFileSize(file))
  }

  function formatAcceptTypesToHumanReadable(accept: string): string {
    const typeMap: Record<string, string> = {
      // Wildcards
      "image/*": "afbeeldingen",
      "video/*": "video's",
      "audio/*": "audiobestanden",
      "text/*": "tekstbestanden",
      // Documents
      "application/pdf": "PDF-bestanden",
      "application/msword": "Word-documenten",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word-documenten",
      "application/vnd.ms-excel": "Excel-bestanden",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel-bestanden",
      "application/vnd.oasis.opendocument.spreadsheet": "ODS-bestanden",
      "text/csv": "CSV-bestanden",
      "text/plain": "tekstbestanden",
      // Images
      "image/jpeg": "JPEG-afbeeldingen",
      "image/jpg": "JPG-afbeeldingen",
      "image/png": "PNG-afbeeldingen",
      "image/gif": "GIF-afbeeldingen",
      "image/bmp": "BMP-afbeeldingen",
      "image/webp": "WebP-afbeeldingen",
      "image/avif": "AVIF-afbeeldingen",
      // Email
      "message/rfc822": "e-mailbestanden",
      "application/vnd.ms-outlook": "Outlook-berichten"
    }

    const types = accept
      .split(",")
      .map((type) => type.trim())
      .map((type) => {
        // Check if it's in our map
        if (typeMap[type]) {
          return typeMap[type]
        }
        // Handle file extensions like .pdf
        if (type.startsWith(".")) {
          return `${type.slice(1).toUpperCase()}-bestanden`
        }
        // Fallback to original
        return type
      })

    // Remove duplicates and join
    return [...new Set(types)].join(", ")
  }

  /**
   * Display an error dialog when one or more files are invalid.
   * @param files - The files that are invalid.
   * @param allowedTypes - The allowed types.
   * @param maximumFileSize - The maximum file size.
   * @returns The error dialog.
   */
  function displayErrorDialog() {
    const allowedTypesText = options?.accept
      ? formatAcceptTypesToHumanReadable(options.accept)
      : getAllowedTypes().join(", ")

    useDialogStore().currentDialog = {
      title: "Één of meerdere bestanden zijn niet geldig",
      body: `Een bestand mag maximaal ${getMaximumFileSize() / 1024 / 1024} MB
        groot zijn en moet een van de volgende bestandstypes hebben:
        ${allowedTypesText}.`,
      type: DialogTypes.OK,
      mandatedWaitTimeSec: 0
    }
  }

  return {
    isUploading,
    upload,
    uploadAndCreateRelation,
    displayErrorDialog
  }
}

function getAllowedTypes(): string[] {
  return [
    // documents
    "pdf",
    "doc",
    "docx",
    "xls",
    "xlsm",
    "xlsx",
    "ods",
    "csv",
    "txt",
    // images
    "jpg",
    "jpeg",
    "png",
    "bmp",
    "gif",
    "avif",
    "webp",
    // email
    "eml",
    "mbox",
    "msg"
  ]
}

function getMaximumFileSize(): number {
  return 1024 * 1024 * 250 // 250 MB
}
